import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MessageSquare, Plus, RefreshCcw, Star } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

function fmtDate(iso) {
  if (!iso) return "\u2014";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function BetaMyFeedback() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    try {
      const r = await api.get("/beta/feedback/mine");
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(errMsg(e, "Could not load your feedback."));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  return (
    <div className="space-y-6" data-testid="beta-myfeedback-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">Industry Beta · My Feedback</div>
          <h1 className="font-display text-3xl tracking-tight flex items-center gap-3">
            <MessageSquare className="w-7 h-7" style={{ color: "hsl(347 60% 22%)" }} />
            My feedback
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            A private history of feedback you have submitted. Other reviewers do not see this list and
            we never share it externally.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={refresh} data-testid="beta-myfeedback-refresh">
            <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          <Button asChild size="sm" data-testid="beta-myfeedback-new">
            <Link to="/beta/feedback"><Plus className="w-3.5 h-3.5 mr-1.5" /> New feedback</Link>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground" data-testid="beta-myfeedback-loading">Loading\u2026</div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-14 text-center text-sm text-muted-foreground" data-testid="beta-myfeedback-empty">
            You have not submitted any feedback yet.{" "}
            <Link to="/beta" className="underline">Browse the catalog</Link> to get started.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="beta-myfeedback-list">
          {rows.map((r) => (
            <Card key={r.id} data-testid={`beta-myfeedback-row-${r.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    {r.track_id ? (
                      <Link to={`/beta/track/${r.track_id}`} className="font-medium hover:underline" data-testid={`beta-myfeedback-track-${r.id}`}>
                        {r.track_title_snapshot || r.track_id}
                      </Link>
                    ) : (
                      <span className="font-medium">General feedback</span>
                    )}
                    {r.track_artist_snapshot && <span className="text-muted-foreground text-[12.5px]"> \u00b7 {r.track_artist_snapshot}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {r.rating ? (
                      <div className="inline-flex items-center gap-0.5" aria-label={`${r.rating} of 5`}>
                        {[1,2,3,4,5].map((n) => (
                          <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-amber-400 stroke-amber-500" : "stroke-slate-400"}`} />
                        ))}
                      </div>
                    ) : null}
                    <span className="text-[11.5px] text-muted-foreground">{fmtDate(r.created_at)}</span>
                  </div>
                </div>
                {r.open_comments && (
                  <p className="text-[12.5px] text-slate-700 whitespace-pre-wrap line-clamp-3">{r.open_comments}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
